// ABOUTME: Form text field component with label and validation support
// ABOUTME: Provides labeled text input with custom placeholder positioning

import * as React from "react"
import type { ReactNode } from "react"
import { Controller, type Control, type FieldValues, type Path, type RegisterOptions } from "react-hook-form"
import { InputBase } from "./CTextInput"
import { CLabel } from "./CLabel"
import { cn } from "@shared/lib/utils"
import type { UseFormRegisterReturn } from "react-hook-form"

// Base props without control
interface CFieldTextBaseProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "color" | "name"> {
  label?: string | ReactNode
  isNegativeVal?: boolean
  classNameWrap?: string
  placeholderCustom?: string
  placeholderCustomStyle?: string
  requiredText?: boolean
  tooltip?: ReactNode
  color?: string
  isInt?: boolean
  isDisablePro?: boolean
  notEnough?: { route: string; description: string; activeTab?: number }
  icon?: React.ComponentType<{ className?: string }>
  helperText?: React.ReactNode
  sizing?: "xs" | "sm" | "md" | "lg"
}

// Props when using register pattern
interface CFieldTextRegisterProps extends CFieldTextBaseProps {
  register?: UseFormRegisterReturn
  control?: never
  rules?: never
  name?: string
}

// Props when using control pattern
interface CFieldTextControlProps<T extends FieldValues> extends CFieldTextBaseProps {
  control: Control<T>
  name: Path<T>
  rules?: RegisterOptions<T, Path<T>>
  register?: never
}

export type CFieldTextProps<T extends FieldValues = FieldValues> = CFieldTextRegisterProps | CFieldTextControlProps<T>

const colorClasses: Record<string, string> = {
  failure: "border-red-500 focus-visible:ring-red-500",
  error: "border-red-500 focus-visible:ring-red-500",
  success: "border-green-500 focus-visible:ring-green-500",
  warning: "border-yellow-500 focus-visible:ring-yellow-500",
}

const sizeClasses: Record<string, string> = {
  xs: "h-[30px] text-xs",
  sm: "h-[30px] text-sm",
  md: "h-[38px] text-base",
  lg: "h-[46px] text-lg",
}

// Navigation/control keys allowed in decimal text inputs
const NAVIGATION_KEYS = [
  "Backspace",
  "Delete",
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "ArrowDown",
  "Tab",
  "Enter",
  "Home",
  "End",
  "Escape",
]

// Inner component for the actual input rendering
const CFieldTextInner = React.forwardRef<
  HTMLInputElement,
  CFieldTextBaseProps & { register?: UseFormRegisterReturn; name?: string }
>((props, ref) => {
  const {
    label,
    className = "",
    placeholderCustomStyle = "",
    required = false,
    type,
    isNegativeVal,
    register,
    classNameWrap,
    placeholderCustom,
    requiredText,
    tooltip,
    isInt,
    isDisablePro,
    notEnough,
    color,
    icon: Icon,
    helperText,
    sizing = "sm",
    ...args
  } = props

  const { ref: refForm, ...rest } = { ...register }
  const normalizedColor = color === "error" ? "failure" : color
  const hasError = normalizedColor === "failure"

  const placeholderCustomStyleRes = `absolute right-2 text-xs font-normal text-[#AFAFAF] ${placeholderCustomStyle}`

  // Local buffer: preserves intermediate typing states (e.g. "7.") for all controlled inputs.
  // Without this, parent onChange doing Number("7.") = 7 would strip the trailing dot immediately.
  const useBuffer = !register && args.value !== undefined
  const [bufferFocused, setBufferFocused] = React.useState(false)
  const [buffer, setBuffer] = React.useState("")

  // Handlers for register's onChange/onBlur (needed when chaining)
  const restOnChange = (rest as { onChange?: (e: { target: unknown; type?: string }) => void }).onChange
  const restOnBlur = (rest as { onBlur?: (e: { target: unknown; type?: string }) => void }).onBlur

  // Buffer value: while focused, always show what user typed (avoids cursor jumps
  // with debounced parents). When not focused, show parent's canonical value.
  const getBufferValue = () => {
    if (!bufferFocused) return args.value ?? ""
    return buffer
  }

  const bufferOverrides = useBuffer
    ? {
        value: getBufferValue(),
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          setBuffer(e.target.value)
          restOnChange?.(e)
          args.onChange?.(e)
        },
        onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
          setBufferFocused(true)
          setBuffer(String(args.value ?? ""))
          args.onFocus?.(e)
        },
        onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
          setBufferFocused(false)
          restOnBlur?.(e)
          args.onBlur?.(e)
        },
      }
    : {}

  // Decimal number mode: type="number" without isInt — keyboard filtering, type change, paste sanitization
  const isDecimalNumber = type === "number" && !isInt && !register

  const decimalOverrides = isDecimalNumber
    ? {
        inputMode: "decimal" as const,
        onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => {
          e.preventDefault()
          const text = e.clipboardData.getData("text/plain")
          const sanitized = text.replace(/,/g, ".").replace(/[^0-9.\-]/g, "")
          const firstDot = sanitized.indexOf(".")
          const clean =
            firstDot === -1
              ? sanitized
              : sanitized.slice(0, firstDot + 1) + sanitized.slice(firstDot + 1).replace(/\./g, "")
          document.execCommand("insertText", false, clean)
        },
      }
    : {}

  return (
    <div
      className={cn("relative w-full", classNameWrap)}
      data-field-error={hasError || undefined}
    >
      {/* The field label of the design system: 13/500 in #2e3033, 6px above the
          control (docs/DESIGN_SYSTEM.md §1). */}
      {label && (
        <CLabel className="mb-1.5 flex items-center text-[13px] font-medium text-secondary-foreground">
          {label}
          {(required || requiredText) && <span className="text-red-700 ml-1">*</span>}
          {tooltip}
        </CLabel>
      )}
      <div className="relative">
        {Icon && (
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <Icon className="h-4 w-4 text-gray-500" />
          </div>
        )}
        <InputBase
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (isInt && type === "number" && (e.key === "," || e.key === ".")) {
              e.preventDefault()
              return
            }
            if (isDecimalNumber) {
              // Comma → dot conversion
              if (e.key === ",") {
                e.preventDefault()
                if (!e.currentTarget.value.includes(".")) {
                  document.execCommand("insertText", false, ".")
                }
                return
              }
              // Allow navigation/control keys and keyboard shortcuts
              if (e.ctrlKey || e.metaKey || NAVIGATION_KEYS.includes(e.key)) return
              // Allow single dot
              if (e.key === ".") {
                if (e.currentTarget.value.includes(".")) e.preventDefault()
                return
              }
              // Allow minus only at position 0 (unless negative values are blocked)
              if (e.key === "-") {
                if (isNegativeVal || (e.currentTarget.selectionStart ?? 0) !== 0) e.preventDefault()
                return
              }
              // Block non-digit characters
              if (!/^[0-9]$/.test(e.key)) {
                e.preventDefault()
              }
              return
            }
            if (isNegativeVal && type === "number" && e.key === "-") {
              e.preventDefault()
            }
          }}
          type={isDecimalNumber ? "text" : type}
          required={required}
          className={cn(
            "bg-white",
            sizeClasses[sizing],
            Icon && "pl-10",
            placeholderCustom && "pl-1.5 pr-6",
            normalizedColor && colorClasses[normalizedColor],
            className,
          )}
          ref={ref || refForm}
          {...rest}
          {...args}
          {...bufferOverrides}
          {...decimalOverrides}
        />
        {placeholderCustom && <span className={`bottom-1.5 ${placeholderCustomStyleRes}`}>{placeholderCustom}</span>}
      </div>
      {helperText && (
        <p className={cn("mt-1 text-sm", normalizedColor === "failure" ? "text-red-600" : "text-gray-500")}>
          {helperText}
        </p>
      )}
    </div>
  )
})

CFieldTextInner.displayName = "CFieldTextInner"

// Main component that handles both register and control patterns
export const CFieldText = React.forwardRef<HTMLInputElement, CFieldTextProps>((props, ref) => {
  // Check if using control pattern
  if ("control" in props && props.control) {
    const { control, name, rules, ...rest } = props

    return (
      <Controller
        control={control}
        name={name}
        rules={rules}
        render={({ field }) => {
          const fieldValue =
            typeof field.value === "string" || typeof field.value === "number" ? String(field.value) : ""
          return (
            <CFieldTextInner
              ref={field.ref}
              name={field.name}
              value={fieldValue}
              onChange={field.onChange}
              onBlur={field.onBlur}
              {...rest}
            />
          )
        }}
      />
    )
  }

  // Using register pattern or uncontrolled
  return (
    <CFieldTextInner
      ref={ref}
      {...props}
    />
  )
}) as <T extends FieldValues = FieldValues>(
  props: CFieldTextProps<T> & { ref?: React.Ref<HTMLInputElement> },
) => React.ReactElement
;(CFieldText as React.FC).displayName = "CFieldText"
